import { dataLayerConfig, TAGButton, TAGLink } from '@aspendental/shared-components-web';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import { Button, Dialog, DialogActions, DialogContent, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { usePatientInformationStore, useStepManagerStore } from '@src/store';
import useAppointmentInformationStore from '@src/store/appointment-information.store';
export type ModalType = 'promptToCall' | 'existingConsultation';
interface IPromptToCallModalProps {
  open: boolean;
  handleClose: () => void;
  modalType: ModalType;
}
export default function PromptToCallModal({
  open,
  handleClose,
  modalType
}: IPromptToCallModalProps) {
  const {
    t
  } = useTranslation('index');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const {
    currentStepName
  } = useStepManagerStore(store => store);
  const {
    salesforceS20,
    leadId,
    salesforceTier
  } = usePatientInformationStore(store => store);
  const {
    presetCenterID,
    presetCenterName
  } = useAppointmentInformationStore();
  const [untrackedPTC, setUntrackedPTC] = useState(true);
  useEffect(() => {
    if (open && modalType === 'promptToCall' && untrackedPTC) {
      setUntrackedPTC(false);
      dataLayerConfig({
        event: 'ptc_modal',
        step_name: currentStepName(),
        lead_id: leadId,
        lead_tier: salesforceS20 && salesforceS20 != 21 ? salesforceTier : 'T1',
        lead_segment: salesforceS20 ?? '21',
        website_selected_center_name: presetCenterName,
        website_selected_center_id: presetCenterID
      });
    }
  }, [open, modalType, untrackedPTC, currentStepName, salesforceS20, leadId, salesforceTier]);
  useEffect(() => {
    if (open) {
      if (modalType === 'existingConsultation') {
        dataLayerConfig({
          event: 'scheduleform_step2_existingappointment'
        });
      }
      if (modalType === 'promptToCall') {
        dataLayerConfig({
          event: 'scheduleform_step3_ptc'
        });
      }
    }
  }, [open, modalType]);
  const title = modalType === 'promptToCall' ? t('speakWithAdvocate') : modalType === 'existingConsultation' ? t('consultAlreadyScheduled') : '';
  const subTitleNode = modalType === 'promptToCall' ? <Typography mb={2} variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}>
				{t('pleaseCallAt')}{' '}
				<TAGLink linkSx={{
      color: theme.palette.text.interactive,
      textDecoration: 'none'
    }} href={`tel:888-486-3443`}>
					888-486-3443
				</TAGLink>{' '}
				{t('toSpeakWithAdvocate')}
			</Typography> : modalType === 'existingConsultation' ? <Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'} color={isSmallScreen ? 'tertiarty' : 'secondary'}>
				{t('wantToCancelOrReschedule')}
				{isSmallScreen && <br />} {t('yourAppointmemnt')}
			</Typography> : '';
  const textNode = modalType === 'promptToCall' ? <Typography variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}>
				{t('informationWillBeSaved')}
				<br />
				{t('thankYou')}
			</Typography> : modalType === 'existingConsultation' ? <Typography mt={2} mb={-2} variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'} color={isSmallScreen ? 'tertiarty' : 'secondary'}>
				{t('pleaseCallAt')}
				{isSmallScreen && <br />}
				<TAGLink linkSx={{
      color: theme.palette.text.interactive,
      textDecoration: 'none'
    }} href={`tel:888-651-9950`}>
					888-651-9950
				</TAGLink>{' '}
				{t('forAssistance')}
			</Typography> : '';
  return <>
			<Dialog open={open} onClose={handleClose} data-test-id="prompt_to_call_modal" data-sentry-element="Dialog" data-sentry-source-file="PromptToCallModal.tsx">
				<DialogContent data-sentry-element="DialogContent" data-sentry-source-file="PromptToCallModal.tsx">
					<Stack direction="column" sx={{
          alignItems: 'center',
          textAlign: 'center',
          py: 3,
          position: 'relative'
        }} data-sentry-element="Stack" data-sentry-source-file="PromptToCallModal.tsx">
						{!isSmallScreen && <IconButton onClick={handleClose} sx={{
            position: 'absolute',
            right: '-10px',
            top: '-10px'
          }}>
								<CloseIcon />
							</IconButton>}
						<ErrorIcon sx={{
            width: 33,
            height: 33
          }} data-sentry-element="ErrorIcon" data-sentry-source-file="PromptToCallModal.tsx" />
						<Typography variant="header3" sx={{
            color: 'text.primary',
            mt: 3,
            mb: 2
          }} data-sentry-element="Typography" data-sentry-source-file="PromptToCallModal.tsx">
							{title}
						</Typography>
						{subTitleNode}
						{textNode}
					</Stack>
				</DialogContent>
				<DialogActions sx={{
        py: 2,
        px: 4,
        backgroundColor: 'backgrounds.neutral',
        justifyContent: {
          md: 'center',
          xs: 'space-between'
        }
      }} data-sentry-element="DialogActions" data-sentry-source-file="PromptToCallModal.tsx">
					<Button variant="tertiaryDefault" onClick={handleClose} data-sentry-element="Button" data-sentry-source-file="PromptToCallModal.tsx">
						{t('cancel')}
					</Button>
					{isSmallScreen && <TAGButton href={`tel:888-651-9950`} sx={{
          mr: 0.5
        }}>
							{t('callUs')}
						</TAGButton>}
				</DialogActions>
			</Dialog>
		</>;
}